/**
 * BrazeEventSyncIntegrationPage
 *
 * Braze is an outbound integration partner: our backend queues events for the
 * Braze REST /users/track endpoint. This page configures the integration and
 * shows a recent-sync feed. Green-only accent.
 *
 * Sections:
 *   - Connection: Braze REST API key + REST endpoint URL (stored client-side only
 *     in this iteration; actual secret storage is owned by a separate route).
 *   - Event mapping table: local event name -> Braze event name (local preset list).
 *   - Test event CTA: enqueues a sample event via POST /api/v1/braze-event-sync-integration.
 *   - Recent sync feed: GET / with optional status filter.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useBrazeEventEnqueue,
	useBrazeEventList,
	useBrazeEventMarkStatus,
} from "./hooks";
import type { BrazeEventRow, BrazeSyncStatus } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-xs font-medium text-zinc-600";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const STATUS_PILL: Record<BrazeSyncStatus, string> = {
	pending:
		"inline-flex items-center rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700",
	synced:
		"inline-flex items-center rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800",
	failed:
		"inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700",
};

const STATUS_FILTERS: ReadonlyArray<{ value: "" | BrazeSyncStatus; label: string }> = [
	{ value: "", label: "All" },
	{ value: "pending", label: "Pending" },
	{ value: "synced", label: "Synced" },
	{ value: "failed", label: "Failed" },
];

// Local event-name preset list. Does not persist to BE — it is only used to
// drive the test-event CTA and a visual mapping table.
const DEFAULT_MAPPING: ReadonlyArray<{ local: string; braze: string }> = [
	{ local: "signup_completed", braze: "signup" },
	{ local: "order_placed", braze: "purchase" },
	{ local: "trial_started", braze: "trial_started" },
	{ local: "subscription_renewed", braze: "subscription_renewed" },
];

function formatTs(seconds: number | null): string {
	if (!seconds) return "-";
	try {
		return new Date(seconds * 1000).toISOString().replace("T", " ").slice(0, 19);
	} catch {
		return "-";
	}
}

function randomEventId(): string {
	return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function BrazeEventSyncIntegrationPage() {
	const [statusFilter, setStatusFilter] = useState<"" | BrazeSyncStatus>("");
	const listQuery = useBrazeEventList(statusFilter || undefined);
	const enqueueMut = useBrazeEventEnqueue();
	const markMut = useBrazeEventMarkStatus();

	// Connection form — client-only in this iteration.
	const [apiKey, setApiKey] = useState("");
	const [endpoint, setEndpoint] = useState("https://rest.iad-01.braze.com");
	const [connectionSaved, setConnectionSaved] = useState(false);

	// Test event form.
	const [testEventName, setTestEventName] = useState("signup_completed");
	const [testExternalId, setTestExternalId] = useState("");

	const mapping = useMemo(() => DEFAULT_MAPPING, []);

	const items: BrazeEventRow[] = listQuery.data?.items ?? [];

	const handleSaveConnection = (e: FormEvent) => {
		e.preventDefault();
		setConnectionSaved(true);
	};

	const handleSendTestEvent = (e: FormEvent) => {
		e.preventDefault();
		if (!testEventName.trim()) return;
		enqueueMut.mutate({
			event_id: randomEventId(),
			event_name: testEventName.trim(),
			external_id: testExternalId.trim() || null,
			properties: { source: "dash_test_event", ts: Math.floor(Date.now() / 1000) },
		});
	};

	return (
		<main
			data-testid="braze-event-sync-integration-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Integration / Braze
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Braze event sync
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Push tracked events to Braze via the REST
					{" "}<code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs">/users/track</code>{" "}
					endpoint. Events are enqueued with status
					{" "}<span className={STATUS_PILL.pending}>pending</span>{" "}
					and flipped to
					{" "}<span className={STATUS_PILL.synced}>synced</span>{" "}
					or
					{" "}<span className={STATUS_PILL.failed}>failed</span>{" "}
					by the worker loop.
				</p>
			</header>

			<section className={CARD} aria-label="Braze connection">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Connection</h2>
					{connectionSaved ? (
						<span
							className={STATUS_PILL.synced}
							data-testid="braze-event-sync-integration-connection-saved"
						>
							Saved
						</span>
					) : null}
				</div>
				<form
					onSubmit={handleSaveConnection}
					className="mt-3 grid gap-3 sm:grid-cols-2"
					data-testid="braze-event-sync-integration-connection-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>REST API key</span>
						<input
							type="password"
							className={INPUT}
							placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
							autoComplete="off"
							value={apiKey}
							onChange={(e) => {
								setApiKey(e.currentTarget.value);
								setConnectionSaved(false);
							}}
							data-testid="braze-event-sync-integration-api-key"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>REST endpoint URL</span>
						<input
							type="url"
							className={INPUT}
							placeholder="https://rest.iad-01.braze.com"
							value={endpoint}
							onChange={(e) => {
								setEndpoint(e.currentTarget.value);
								setConnectionSaved(false);
							}}
							data-testid="braze-event-sync-integration-endpoint"
						/>
					</label>
					<div className="sm:col-span-2 flex justify-end">
						<button
							type="submit"
							className={BTN}
							disabled={!apiKey.trim() || !endpoint.trim()}
							data-testid="braze-event-sync-integration-save-connection"
						>
							Save connection
						</button>
					</div>
				</form>
			</section>

			<section className={CARD} aria-label="Event mapping">
				<h2 className="text-base font-semibold text-zinc-900">Event mapping</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Local event name -&gt; Braze event name. Preset list; editing not yet supported.
				</p>
				<div className="mt-3 overflow-x-auto">
					<table
						className="min-w-full text-sm"
						data-testid="braze-event-sync-integration-mapping-table"
					>
						<thead>
							<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="py-2 pr-4 font-medium">Local event</th>
								<th className="py-2 pr-4 font-medium">Braze event</th>
							</tr>
						</thead>
						<tbody>
							{mapping.map((m) => (
								<tr
									key={m.local}
									className="border-b border-zinc-100 last:border-b-0"
									data-testid={`braze-event-sync-integration-mapping-row-${m.local}`}
								>
									<td className="py-2 pr-4 font-mono text-xs text-zinc-800">{m.local}</td>
									<td className="py-2 pr-4 font-mono text-xs text-zinc-800">{m.braze}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

			<section className={CARD} aria-label="Send test event">
				<h2 className="text-base font-semibold text-zinc-900">Send test event</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Enqueues a sample event. Worker picks it up and pushes to Braze.
				</p>
				<form
					onSubmit={handleSendTestEvent}
					className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto]"
					data-testid="braze-event-sync-integration-test-event-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Event name</span>
						<input
							type="text"
							className={INPUT}
							placeholder="signup_completed"
							value={testEventName}
							onChange={(e) => setTestEventName(e.currentTarget.value)}
							disabled={enqueueMut.isPending}
							data-testid="braze-event-sync-integration-test-event-name"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>External ID (optional)</span>
						<input
							type="text"
							className={INPUT}
							placeholder="user_123"
							value={testExternalId}
							onChange={(e) => setTestExternalId(e.currentTarget.value)}
							disabled={enqueueMut.isPending}
							data-testid="braze-event-sync-integration-test-external-id"
						/>
					</label>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN}
							disabled={enqueueMut.isPending || !testEventName.trim()}
							data-testid="braze-event-sync-integration-send-test-event"
						>
							{enqueueMut.isPending ? "Sending." : "Send test event"}
						</button>
					</div>
				</form>
				{enqueueMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="braze-event-sync-integration-test-event-error"
					>
						{enqueueMut.error.message}
					</p>
				) : null}
				{enqueueMut.isSuccess && enqueueMut.data ? (
					<p
						className="mt-2 text-xs text-green-700"
						data-testid="braze-event-sync-integration-test-event-success"
					>
						Enqueued {enqueueMut.data.event_id} ({enqueueMut.data.status}).
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Recent sync feed">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<h2 className="text-base font-semibold text-zinc-900">Recent sync feed</h2>
					<div
						className="flex items-center gap-1"
						data-testid="braze-event-sync-integration-status-filter"
						role="group"
						aria-label="Status filter"
					>
						{STATUS_FILTERS.map((f) => {
							const active = f.value === statusFilter;
							return (
								<button
									key={f.value || "all"}
									type="button"
									onClick={() => setStatusFilter(f.value)}
									className={
										active
											? "rounded-[6px] border border-green-700 bg-green-700 px-2.5 py-1 text-xs font-medium text-white"
											: BTN_GHOST
									}
									data-testid={`braze-event-sync-integration-filter-${f.value || "all"}`}
								>
									{f.label}
								</button>
							);
						})}
					</div>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-event-sync-integration-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="braze-event-sync-integration-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-event-sync-integration-empty"
					>
						No events yet. Send a test event to see it here.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="min-w-full text-sm"
							data-testid="braze-event-sync-integration-list"
						>
							<thead>
								<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
									<th className="py-2 pr-4 font-medium">Event</th>
									<th className="py-2 pr-4 font-medium">External ID</th>
									<th className="py-2 pr-4 font-medium">Status</th>
									<th className="py-2 pr-4 font-medium">Created</th>
									<th className="py-2 pr-4 font-medium">Synced</th>
									<th className="py-2 pr-4 font-medium">Actions</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<tr
										key={row.id}
										className="border-b border-zinc-100 last:border-b-0"
										data-testid={`braze-event-sync-integration-row-${row.id}`}
									>
										<td className="py-2 pr-4">
											<div className="font-mono text-xs text-zinc-800">{row.event_name}</div>
											<div className="font-mono text-[10px] text-zinc-400">{row.event_id}</div>
										</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-700">
											{row.external_id ?? "-"}
										</td>
										<td className="py-2 pr-4">
											<span className={STATUS_PILL[row.sync_status]}>{row.sync_status}</span>
										</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
											{formatTs(row.created_at)}
										</td>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-600">
											{formatTs(row.synced_at)}
										</td>
										<td className="py-2 pr-4">
											{row.sync_status === "pending" ? (
												<div className="flex gap-1">
													<button
														type="button"
														className={BTN_GHOST}
														disabled={markMut.isPending}
														onClick={() =>
															markMut.mutate({
																eventId: row.event_id,
																body: { status: "synced" },
															})
														}
														data-testid={`braze-event-sync-integration-mark-synced-${row.id}`}
													>
														Mark synced
													</button>
													<button
														type="button"
														className={BTN_GHOST}
														disabled={markMut.isPending}
														onClick={() =>
															markMut.mutate({
																eventId: row.event_id,
																body: { status: "failed" },
															})
														}
														data-testid={`braze-event-sync-integration-mark-failed-${row.id}`}
													>
														Mark failed
													</button>
												</div>
											) : (
												<span className="text-xs text-zinc-400">-</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default BrazeEventSyncIntegrationPage;
